import { useState, useCallback, useMemo } from 'react';
import type { Expense, ExpenseInput, CategoryOption } from '../hooks/useExpenses';

/* ── Constants ────────────────────────────────────────────────────── */

const CATEGORY_COLORS: Record<string, string> = {
  subscription:   '#a78bfa',
  utilities:      '#facc15',
  food:           '#4ade80',
  transport:      '#22d3ee',
  health:         '#f87171',
  tech:           '#38bdf8',
  education:      '#e879f9',
  housing:        '#fb923c',
  entertainment:  '#c084fc',
  payroll:        '#34d399',
  marketing:      '#f472b6',
  office:         '#94a3b8',
  other:          '#6b7280',
};

const PAYMENT_METHODS = [
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'crypto', label: 'Crypto' },
];

const CURRENCIES = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'ARS', label: 'ARS' },
  { value: 'BRL', label: 'BRL' },
];

const RECURRING_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'yearly', label: 'Yearly' },
];

interface Props {
  expenses: Expense[];
  categories: CategoryOption[];
  loading: boolean;
  saving: boolean;
  onSave: (input: ExpenseInput) => void;
  onDelete: (id: string) => void;
  categoryFilter: string;
  onCategoryFilter: (cat: string) => void;
}

export default function ExpenseList({
  expenses,
  categories,
  loading,
  saving,
  onSave,
  onDelete,
  categoryFilter,
  onCategoryFilter,
}: Props) {
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [showNew, setShowNew] = useState(false);

  const handleNew = useCallback(() => {
    setShowNew(true);
    setEditingExpense(null);
  }, []);

  const handleEdit = useCallback((exp: Expense) => {
    setEditingExpense(exp);
    setShowNew(false);
  }, []);

  const handleClose = useCallback(() => {
    setEditingExpense(null);
    setShowNew(false);
  }, []);

  return (
    <div className="exp-list-root">
      {/* Toolbar */}
      <div className="exp-list-toolbar">
        <div className="exp-list-filters">
          <select
            className="exp-filter-select"
            value={categoryFilter}
            onChange={(e) => onCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          {loading && <span className="exp-loading-dot" />}
        </div>
        <button type="button" className="exp-add-btn" onClick={handleNew}>
          + Add Expense
        </button>
      </div>

      {/* Table */}
      <div className="exp-table-wrap">
        <table className="exp-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Category</th>
              <th className="exp-th-right">Amount</th>
              <th>Method</th>
              <th>Source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="exp-empty">
                  No expenses found. Add your first expense or use agent-chat!
                </td>
              </tr>
            )}
            {expenses.map((exp) => (
              <tr key={exp.id} className="exp-row" onClick={() => handleEdit(exp)}>
                <td className="exp-cell-date">{exp.date}</td>
                <td className="exp-cell-desc">
                  {exp.description}
                  {exp.recurring !== 'none' && (
                    <span className="exp-recurring-badge" title={`Recurring: ${exp.recurring}`}>
                      {exp.recurring}
                    </span>
                  )}
                </td>
                <td>
                  <span
                    className="exp-cat-badge"
                    style={{ background: CATEGORY_COLORS[exp.category] || '#6b7280' }}
                  >
                    {exp.category}
                  </span>
                </td>
                <td className="exp-cell-amount">
                  <span className="exp-currency">{exp.currency}</span>
                  {exp.amount.toFixed(2)}
                </td>
                <td className="exp-cell-method">{exp.payment_method}</td>
                <td className="exp-cell-source">{exp.source}</td>
                <td className="exp-cell-actions">
                  <button
                    type="button"
                    className="exp-del-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(exp.id);
                    }}
                  >
                    &times;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor overlay */}
      {(editingExpense || showNew) && (
        <ExpenseEditor
          expense={editingExpense}
          categories={categories}
          saving={saving}
          onSave={(input) => {
            onSave(input);
            handleClose();
          }}
          onClose={handleClose}
        />
      )}
    </div>
  );
}

/* ── Expense Editor ───────────────────────────────────────────────── */

function ExpenseEditor({
  expense,
  categories,
  saving,
  onSave,
  onClose,
}: {
  expense: Expense | null;
  categories: CategoryOption[];
  saving: boolean;
  onSave: (input: ExpenseInput) => void;
  onClose: () => void;
}) {
  const isExisting = !!expense?.id;
  const [description, setDescription] = useState(expense?.description || '');
  const [amount, setAmount] = useState(expense?.amount?.toString() || '');
  const [currency, setCurrency] = useState(expense?.currency || 'USD');
  const [category, setCategory] = useState(expense?.category || 'other');
  const [date, setDate] = useState(expense?.date || new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState(expense?.payment_method || 'card');
  const [recurring, setRecurring] = useState(expense?.recurring || 'none');
  const [notes, setNotes] = useState(expense?.notes || '');
  const [vendor, setVendor] = useState(expense?.vendor || '');

  const isValid = description.trim().length > 0 && parseFloat(amount) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(date);

  const handleSave = useCallback(() => {
    if (!isValid) return;
    onSave({
      id: expense?.id || undefined,
      description: description.trim(),
      amount: parseFloat(amount),
      currency,
      category,
      date,
      payment_method: paymentMethod,
      recurring,
      notes,
      vendor,
    });
  }, [expense, description, amount, currency, category, date, paymentMethod, recurring, notes, vendor, isValid, onSave]);

  return (
    <div className="exp-editor-overlay" onClick={onClose}>
      <div className="exp-editor" onClick={(e) => e.stopPropagation()}>
        <div className="exp-editor__header">
          <span
            className="exp-editor__badge"
            style={{ background: CATEGORY_COLORS[category] || '#6b7280' }}
          />
          <span className="exp-editor__title-label">
            {isExisting ? 'Edit Expense' : 'New Expense'}
          </span>
        </div>

        {/* Description */}
        <input
          className="exp-editor__input"
          placeholder="What did you spend on?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && isValid) handleSave();
            if (e.key === 'Escape') onClose();
          }}
        />

        {/* Amount + Currency row */}
        <div className="exp-editor__row exp-editor__amount-row">
          <div className="exp-editor__field" style={{ flex: 2 }}>
            <label className="exp-editor__label">Amount</label>
            <input
              type="number"
              className="exp-editor__input"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="exp-editor__field" style={{ flex: 1 }}>
            <label className="exp-editor__label">Currency</label>
            <select
              className="exp-editor__select"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Category + Date row */}
        <div className="exp-editor__row exp-editor__cat-date-row">
          <div className="exp-editor__field" style={{ flex: 1 }}>
            <label className="exp-editor__label">Category</label>
            <select
              className="exp-editor__select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div className="exp-editor__field" style={{ flex: 1 }}>
            <label className="exp-editor__label">Date</label>
            <input
              type="date"
              className="exp-editor__input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        {/* Payment method + Recurring row */}
        <div className="exp-editor__row">
          <div className="exp-editor__field" style={{ flex: 1 }}>
            <label className="exp-editor__label">Payment</label>
            <select
              className="exp-editor__select"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="exp-editor__field" style={{ flex: 1 }}>
            <label className="exp-editor__label">Recurring</label>
            <select
              className="exp-editor__select"
              value={recurring}
              onChange={(e) => setRecurring(e.target.value as Expense['recurring'])}
            >
              {RECURRING_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Vendor */}
        <div className="exp-editor__row">
          <div className="exp-editor__field" style={{ flex: 1 }}>
            <label className="exp-editor__label">Vendor (optional)</label>
            <input
              className="exp-editor__input"
              placeholder="e.g. Netflix, Uber"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
            />
          </div>
        </div>

        {/* Notes */}
        <textarea
          className="exp-editor__input exp-editor__notes"
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />

        {/* Actions */}
        <div className="exp-editor__actions">
          <button
            type="button"
            className="exp-editor__save"
            disabled={!isValid || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : isExisting ? 'Update' : 'Save'}
          </button>
          <button type="button" className="exp-editor__cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
